import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import "./BillboardCalculator.css";

// ---------------------------------------------------------------------------
// Reference data — taken directly from the master flowchart
// ("Flowchart of the Web-Based LED Screen Sizing and Power Calculation
// System"). Module dimensions/resolutions and the standard ratios (16
// modules/cabinet, 16 modules/receiver card, 6 modules/5V power supply) are
// the values given in that document. Controller pixel capacities are
// editable — only the VX4S figure is taken from the flowchart's own worked
// example; the rest are placeholders until you confirm real datasheet specs.
// ---------------------------------------------------------------------------
const MODULE_TYPES = [
  { id: "P2.5", label: "P2.5", widthMM: 320, heightMM: 160, resH: 128, resV: 64 },
  { id: "P3",   label: "P3",   widthMM: 192, heightMM: 192, resH: 64,  resV: 64 },
  { id: "P4",   label: "P4",   widthMM: 256, heightMM: 128, resH: 64,  resV: 32 },
  { id: "P5",   label: "P5",   widthMM: 320, heightMM: 160, resH: 64,  resV: 32 },
  { id: "P6",   label: "P6",   widthMM: 192, heightMM: 192, resH: 32,  resV: 32 },
  { id: "P10",  label: "P10",  widthMM: 320, heightMM: 160, resH: 32,  resV: 16 },
];

const STANDARD_MODULES_PER_CABINET = 16;
const STANDARD_MODULES_PER_RECEIVER_CARD = 16;
const STANDARD_MODULES_PER_PSU = 6; // 5V power supply

// Choose a controller whose capacity (pixels) >= total pixels on screen.
// NovaStar VX4S @ 2,600,000 px is the flowchart's own worked example.
const CONTROLLER_OPTIONS = [
  { label: "NovaStar TB2", maxPixels: 650_000 },
  { label: "NovaStar TB6", maxPixels: 1_300_000 },
  { label: "NovaStar VX4S", maxPixels: 2_600_000 },
  { label: "NovaStar VX6S", maxPixels: 4_300_000 },
  { label: "NovaStar MCTRL4K", maxPixels: 8_300_000 },
];

const PX_PER_METER = 60; // canvas drawing scale
const MAX_CANVAS_W = 640;
const MAX_CANVAS_H = 380;

function ceilDiv(a, b) {
  if (!b || b <= 0) return 0;
  return Math.ceil(a / b);
}

function pickController(totalPixels, options) {
  for (const tier of options) {
    if (totalPixels <= tier.maxPixels) return tier;
  }
  return { label: "Multiple controllers required (split screen)", maxPixels: Infinity };
}

export default function BillboardCalculator() {
  const [widthM, setWidthM] = useState(6);
  const [heightM, setHeightM] = useState(4);
  const [moduleId, setModuleId] = useState("P10");
  const [modulesPerCabinet, setModulesPerCabinet] = useState(STANDARD_MODULES_PER_CABINET);
  const [modulesPerReceiverCard, setModulesPerReceiverCard] = useState(STANDARD_MODULES_PER_RECEIVER_CARD);
  const [modulesPerPSU, setModulesPerPSU] = useState(STANDARD_MODULES_PER_PSU);
  const [powerPerModuleW, setPowerPerModuleW] = useState(120);

  const canvasRef = useRef(null);
  const draggingRef = useRef(false);

  const moduleSpec = useMemo(
    () => MODULE_TYPES.find((m) => m.id === moduleId) || MODULE_TYPES[0],
    [moduleId]
  );

  // -------------------------------------------------------------------------
  // Step 1 — validate input
  // -------------------------------------------------------------------------
  const errors = [];
  if (!widthM || widthM <= 0) errors.push("Screen width must be greater than 0 m.");
  if (!heightM || heightM <= 0) errors.push("Screen height must be greater than 0 m.");
  if (widthM > 200) errors.push("Screen width looks unrealistic (> 200 m).");
  if (heightM > 100) errors.push("Screen height looks unrealistic (> 100 m).");
  const isValid = errors.length === 0;

  // -------------------------------------------------------------------------
  // Steps 3–8 — calculations, following the master flowchart exactly:
  //   Nh = ceil(Width_cm / ModuleWidth_cm)
  //   Nv = ceil(Height_cm / ModuleHeight_cm)
  //   Rh = Nh × Ph      Rv = Nv × Pv      Pt = Rh × Rv
  //   Nm = Nh × Nv
  //   Nc   = ceil(Nm / modulesPerCabinet)          [standard: 16]
  //   Nr   = ceil(Nm / modulesPerReceiverCard)     [standard: 16]
  //   Npsu = ceil(Nm / modulesPerPSU)              [standard: 6, 5V]
  //   Controller: smallest capacity >= Pt
  // -------------------------------------------------------------------------
  const results = useMemo(() => {
    if (!isValid) return null;

    const widthCM = widthM * 100;
    const heightCM = heightM * 100;
    const moduleWidthCM = moduleSpec.widthMM / 10;
    const moduleHeightCM = moduleSpec.heightMM / 10;

    const modulesH = ceilDiv(widthCM, moduleWidthCM);   // Nh
    const modulesV = ceilDiv(heightCM, moduleHeightCM); // Nv
    const totalModules = modulesH * modulesV;           // Nm

    const horizontalPixels = modulesH * moduleSpec.resH; // Rh
    const verticalPixels = modulesV * moduleSpec.resV;   // Rv
    const totalPixels = horizontalPixels * verticalPixels; // Pt

    const totalCabinets = ceilDiv(totalModules, modulesPerCabinet);       // Nc
    const totalReceiverCards = ceilDiv(totalModules, modulesPerReceiverCard); // Nr
    const numberOfPSUs = ceilDiv(totalModules, modulesPerPSU);           // Npsu

    const controller = pickController(totalPixels, CONTROLLER_OPTIONS);

    const estimatedPowerW = totalModules * powerPerModuleW;

    return {
      widthCM,
      heightCM,
      moduleWidthCM,
      moduleHeightCM,
      modulesH,
      modulesV,
      totalModules,
      horizontalPixels,
      verticalPixels,
      totalPixels,
      totalCabinets,
      totalReceiverCards,
      numberOfPSUs,
      controller,
      estimatedPowerW,
      actualScreenWidthM: (modulesH * moduleWidthCM) / 100,
      actualScreenHeightM: (modulesV * moduleHeightCM) / 100,
    };
  }, [isValid, widthM, heightM, moduleSpec, modulesPerCabinet, modulesPerReceiverCard, modulesPerPSU, powerPerModuleW]);

  // -------------------------------------------------------------------------
  // Canvas drawing
  // -------------------------------------------------------------------------
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const cw = canvas.width;
    const ch = canvas.height;
    ctx.clearRect(0, 0, cw, ch);

    // grid, 1 line per meter
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= cw; x += PX_PER_METER) {
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, ch);
      ctx.stroke();
    }
    for (let y = 0; y <= ch; y += PX_PER_METER) {
      ctx.beginPath();
      ctx.moveTo(0, y + 0.5);
      ctx.lineTo(cw, y + 0.5);
      ctx.stroke();
    }

    const rectW = Math.min(widthM * PX_PER_METER, cw - 4);
    const rectH = Math.min(heightM * PX_PER_METER, ch - 4);
    const x0 = 2;
    const y0 = 2;

    // billboard rectangle
    const grad = ctx.createLinearGradient(x0, y0, x0 + rectW, y0 + rectH);
    grad.addColorStop(0, "#1c3a5e");
    grad.addColorStop(1, "#0f2542");
    ctx.fillStyle = grad;
    ctx.fillRect(x0, y0, rectW, rectH);

    ctx.strokeStyle = isValid ? "#4fd1c5" : "#e2555a";
    ctx.lineWidth = 2;
    ctx.strokeRect(x0, y0, rectW, rectH);

    // module tiling preview
    if (isValid && results) {
      ctx.strokeStyle = "rgba(79, 209, 197, 0.35)";
      ctx.lineWidth = 1;
      const mw = (moduleSpec.widthMM / 1000) * PX_PER_METER;
      const mh = (moduleSpec.heightMM / 1000) * PX_PER_METER;
      for (let x = mw; x < rectW; x += mw) {
        ctx.beginPath();
        ctx.moveTo(x0 + x, y0);
        ctx.lineTo(x0 + x, y0 + rectH);
        ctx.stroke();
      }
      for (let y = mh; y < rectH; y += mh) {
        ctx.beginPath();
        ctx.moveTo(x0, y0 + y);
        ctx.lineTo(x0 + rectW, y0 + y);
        ctx.stroke();
      }
    }

    // dimension labels
    ctx.fillStyle = "#e8f1f8";
    ctx.font = "12px 'JetBrains Mono', monospace";
    ctx.fillText(`${widthM.toFixed(2)} m`, x0 + rectW / 2 - 20, y0 + rectH + 16);
    ctx.save();
    ctx.translate(x0 + rectW + 14, y0 + rectH / 2 + 20);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(`${heightM.toFixed(2)} m`, 0, 0);
    ctx.restore();
  }, [widthM, heightM, isValid, results, moduleSpec]);

  useEffect(() => {
    draw();
  }, [draw]);

  // -------------------------------------------------------------------------
  // Drag-to-draw on canvas
  // -------------------------------------------------------------------------
  const pointToMeters = (evt) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    const x = ((clientX - rect.left) / rect.width) * canvas.width;
    const y = ((clientY - rect.top) / rect.height) * canvas.height;
    return { w: Math.max(0.1, x / PX_PER_METER), h: Math.max(0.1, y / PX_PER_METER) };
  };

  const handlePointerDown = (evt) => {
    draggingRef.current = true;
    const { w, h } = pointToMeters(evt);
    setWidthM(Number(w.toFixed(2)));
    setHeightM(Number(h.toFixed(2)));
  };
  const handlePointerMove = (evt) => {
    if (!draggingRef.current) return;
    const { w, h } = pointToMeters(evt);
    setWidthM(Number(w.toFixed(2)));
    setHeightM(Number(h.toFixed(2)));
  };
  const stopDragging = () => {
    draggingRef.current = false;
  };

  return (
    <div className="bc-root">
      <header className="bc-header">
        <span className="bc-eyebrow">LED Screen Sizing &amp; Power Calculation</span>
        <h1>Billboard Component Calculator</h1>
        <p>Drag on the canvas or type dimensions in meters. Modules, cabinets, receiver cards, controller and power supplies follow the standard sizing flowchart.</p>
      </header>

      <div className="bc-layout">
        {/* ---------------- LEFT: canvas + inputs ---------------- */}
        <section className="bc-panel">
          <div className="bc-canvas-wrap">
            <canvas
              ref={canvasRef}
              width={MAX_CANVAS_W}
              height={MAX_CANVAS_H}
              className="bc-canvas"
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={stopDragging}
              onMouseLeave={stopDragging}
              onTouchStart={handlePointerDown}
              onTouchMove={handlePointerMove}
              onTouchEnd={stopDragging}
            />
            <span className="bc-canvas-hint">1 grid square = 1 m · click &amp; drag to size</span>
          </div>

          <div className="bc-field-grid">
            <label className="bc-field">
              <span>Screen width (m)</span>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={widthM}
                onChange={(e) => setWidthM(Number(e.target.value))}
              />
            </label>
            <label className="bc-field">
              <span>Screen height (m)</span>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={heightM}
                onChange={(e) => setHeightM(Number(e.target.value))}
              />
            </label>

            <label className="bc-field bc-field--wide">
              <span>LED module type</span>
              <select value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
                {MODULE_TYPES.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label} · {m.widthMM}×{m.heightMM}mm · {m.resH}×{m.resV}px
                  </option>
                ))}
              </select>
            </label>

            <label className="bc-field">
              <span>Modules / cabinet</span>
              <input
                type="number"
                min="1"
                value={modulesPerCabinet}
                onChange={(e) => setModulesPerCabinet(Number(e.target.value) || 1)}
              />
            </label>

            <label className="bc-field">
              <span>Modules / receiver card</span>
              <input
                type="number"
                min="1"
                value={modulesPerReceiverCard}
                onChange={(e) => setModulesPerReceiverCard(Number(e.target.value) || 1)}
              />
            </label>

            <label className="bc-field">
              <span>Modules / 5V power supply</span>
              <input
                type="number"
                min="1"
                value={modulesPerPSU}
                onChange={(e) => setModulesPerPSU(Number(e.target.value) || 1)}
              />
            </label>

            <label className="bc-field">
              <span>Power per module (W)</span>
              <input
                type="number"
                min="1"
                value={powerPerModuleW}
                onChange={(e) => setPowerPerModuleW(Number(e.target.value) || 1)}
              />
            </label>
          </div>

          {!isValid && (
            <div className="bc-error-box">
              <strong>Fix the following:</strong>
              <ul>
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </section>

        {/* ---------------- RIGHT: results ---------------- */}
        <section className="bc-panel bc-results">
          {!results ? (
            <div className="bc-placeholder">Enter a valid width and height to see the calculated bill of materials.</div>
          ) : (
            <>
              <div className="bc-note">
                Actual built size: <strong>{results.actualScreenWidthM.toFixed(2)} m × {results.actualScreenHeightM.toFixed(2)} m</strong> (rounded up to whole modules — {results.modulesH} × {results.modulesV})
              </div>

              <div className="bc-stat-grid">
                <Stat label="Resolution" value={`${results.horizontalPixels.toLocaleString()} × ${results.verticalPixels.toLocaleString()} px`} sub={`${results.totalPixels.toLocaleString()} px total`} />
                <Stat label="LED Modules" value={results.totalModules.toLocaleString()} sub={`${results.modulesH} across × ${results.modulesV} down`} />
                <Stat label="Cabinets" value={results.totalCabinets.toLocaleString()} sub={`${modulesPerCabinet} modules / cabinet`} />
                <Stat label="Receiver Cards" value={results.totalReceiverCards.toLocaleString()} sub={`${modulesPerReceiverCard} modules / card`} />
                <Stat label="Controller" value={results.controller.label} sub={`cap. ≥ ${results.totalPixels.toLocaleString()} px`} />
                <Stat label="5V Power Supplies" value={results.numberOfPSUs.toLocaleString()} sub={`${modulesPerPSU} modules / PSU`} />
                <Stat label="Estimated Power" value={`${results.estimatedPowerW.toLocaleString()} W`} sub={`@ ${powerPerModuleW} W / module`} />
              </div>

              <h2>Bill of Materials</h2>
              <table className="bc-bom">
                <thead>
                  <tr><th>Item</th><th>Spec</th><th>Qty</th></tr>
                </thead>
                <tbody>
                  <tr><td>LED Modules</td><td>{moduleSpec.label} ({moduleSpec.widthMM}×{moduleSpec.heightMM}mm)</td><td>{results.totalModules}</td></tr>
                  <tr><td>Cabinets</td><td>{modulesPerCabinet} modules each</td><td>{results.totalCabinets}</td></tr>
                  <tr><td>Receiver Cards</td><td>{modulesPerReceiverCard} modules each</td><td>{results.totalReceiverCards}</td></tr>
                  <tr><td>Controller</td><td>{results.controller.label}</td><td>1</td></tr>
                  <tr><td>5V Power Supplies</td><td>{modulesPerPSU} modules each</td><td>{results.numberOfPSUs}</td></tr>
                  <tr><td>Cables / Accessories</td><td>as required</td><td>—</td></tr>
                </tbody>
              </table>
              <p className="bc-disclaimer">
                Module dimensions/resolutions and the 16-module cabinet, 16-module receiver-card, and 6-module 5V-PSU ratios follow the standard sizing flowchart. Controller pixel capacities are editable — confirm your actual model's datasheet before ordering.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bc-stat">
      <span className="bc-stat-label">{label}</span>
      <span className="bc-stat-value">{value}</span>
      {sub && <span className="bc-stat-sub">{sub}</span>}
    </div>
  );
}